/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';

const messages = defineMessages({
    introText: {
        id: 'entityMigration.introText',
        defaultMessage: 'Upgrading your composite entities is seamless and reversible:'
    },
    revisable: {
        id: 'entityMigration.revisable',
        defaultMessage: 'Reversible.'
    },
    revisableDescription: {
        id: 'entityMigration.revisableDescription',
        defaultMessage: 'We create a new version of your app. You can roll back to your previous version at any time.'
    },
    noCodeChanges: {
        id: 'entityMigration.noCodeChanges',
        defaultMessage: 'No changes to code.'
    },
    noCodeChangeDescription: {
        id: 'entityMigration.noCodeChangeDescription',
        defaultMessage: 'You updated entities using the same naming as your composite entities, so you do not need to change your code.'
    },
    noRelabeling: {
        id: 'entityMigration.noRelabeling',
        defaultMessage: 'No relabeling.'
    },
    noRelabelingDescription: {
        id: 'entityMigration.noRelabelingDescription',
        defaultMessage: 'We reuse all your labels, so do not need to re-label entities.'
    }
});

const { styleUlist, styleLi } = getStylistV2('IntroScreen');
const ListItem = styleLi('ListItem', { margin: '8px 0' });
const ShiftedUList = styleUlist('ShiftedUList', { margin: 0, listStyle: 'unset', paddingInlineStart: '15px' });

/**
 * Displays the initial screen that illustrates what happens
 * when the migration occurred.
 */
export const IntroScreen = () => {
    const { localize } = useLocalization();

    return (
        <Stack styles={{ root: { minHeight: '380px' } }} tokens={{ childrenGap: 12 }}>
            <LuisText lineHeight="20px">{localize(messages.introText)}</LuisText>
            <ShiftedUList>
                <ListItem>
                    <LuisText fontWeight="bold">{localize(messages.revisable)}</LuisText>
                    &nbsp;
                    <LuisText>{localize(messages.revisableDescription)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText fontWeight="bold">{localize(messages.noCodeChanges)}</LuisText>
                    &nbsp;
                    <LuisText>{localize(messages.noCodeChangeDescription)}</LuisText>
                </ListItem>
                <ListItem>
                    <LuisText fontWeight="bold">{localize(messages.noRelabeling)}</LuisText>
                    &nbsp;
                    <LuisText>{localize(messages.noRelabelingDescription)}</LuisText>
                </ListItem>
            </ShiftedUList>
        </Stack>
    );
};
